import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { forwardRef, Fragment, ReactNode } from 'react'

import { cn } from '@/app/utils'

import { ButtonSkeleton } from '../../skeleton'
import styles from './Drawer.module.css'
import { DrawerThemes } from './Drawer.types'

type Props = {
  title: ReactNode
  children?: ReactNode
  beforeContentRender?: ReactNode
  beforeContentRef?: (ref: HTMLDivElement | null) => void
  opened: boolean
  isVisibleCloseButton?: boolean
  onClose: () => void
  theme?: DrawerThemes
  className?: string
  contentWrapperClassName?: string
  titleClassName?: string
  closeButtonDisabled?: boolean
  isLoading?: boolean
}

export const drawerDuration = 300
export const drawerScrollableContentWrapperId =
  'drawerScrollableContentWrapperId'

type CloseButtonProps = {
  isLoading: boolean
  handleCloseModal: () => void
  closeButtonDisabled?: boolean
}

const CloseButton = ({
  isLoading,
  handleCloseModal,
  closeButtonDisabled
}: CloseButtonProps) => {
  return (
    <>
      {isLoading && (
        <ButtonSkeleton className="ml-auto flex h-40 w-40 p-8 xs:h-28 xs:w-28" />
      )}
      {!isLoading && (
        <button
          aria-label="Закрыть боковое меню"
          title="Закрыть"
          className={cn(
            'ml-auto flex h-40 w-40 items-center justify-center rounded-100 bg-[rgba(96,107,118,0.10)] p-8 outline-none transition-colors hover:bg-grey-30',
            'sm:h-28 sm:w-28 sm:p-4'
          )}
          onClick={handleCloseModal}
          disabled={closeButtonDisabled}
        >
          <XMarkIcon className="h-24 w-24 text-bronze-70" />
        </button>
      )}
    </>
  )
}

const AppDrawer = forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      children,
      isVisibleCloseButton = true,
      beforeContentRender,
      beforeContentRef,
      opened,
      onClose,
      theme = 'grey',
      className,
      contentWrapperClassName,
      closeButtonDisabled,
      isLoading = false,
      titleClassName
    },
    ref
  ) => (
    <Transition appear show={opened} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50 cursor-pointer"
        onClose={onClose}
      >
        <Transition.Child
          enter="duration-75 xs:duration-300"
          enterFrom="opacity-0"
          leave="duration-75 delay-300 xs:duration-300 xs:delay-0"
          leaveTo="opacity-0"
        >
          <div className={cn('fixed inset-0 bg-black/50 xs:bg-transparent')} />
        </Transition.Child>

        <div className="fixed inset-0 bottom-0 z-50 sm:top-0">
          <div className="flex h-full max-h-full justify-end">
            <Transition.Child
              as={Fragment}
              enter="duration-300 delay-100 xs:delay-0"
              enterFrom="translate-x-[100%] xs:translate-x-0 xs:translate-y-16 xs:opacity-0"
              enterTo="translate-x-0 xs:translate-y-0"
              leave="duration-300"
              leaveTo="translate-x-[100%] xs:translate-x-0 xs:translate-y-16 xs:opacity-0"
            >
              <Dialog.Panel
                className={cn(
                  'relative flex h-full w-[790px] max-w-full cursor-auto flex-col overflow-y-auto rounded-l-20 lg:w-[684px] sm:w-full sm:rounded-0',
                  styles[`${theme}Bg`],
                  className
                )}
              >
                <div
                  ref={ref}
                  className={cn(
                    'flex w-full items-start p-40 pb-24',
                    'lg:p-24 lg:pb-20',
                    'sm:px-16 sm:py-20',
                    titleClassName
                  )}
                >
                  <Dialog.Title className="font-bold text-36 sm:text-20">
                    {title}
                  </Dialog.Title>
                  {isVisibleCloseButton ? (
                    <CloseButton
                      isLoading={isLoading}
                      handleCloseModal={onClose}
                      closeButtonDisabled={closeButtonDisabled}
                    />
                  ) : null}
                </div>

                {beforeContentRender && (
                  <div
                    ref={beforeContentRef}
                    className="pl-44 pr-28 lg:px-24 xs:px-16"
                  >
                    {beforeContentRender}
                  </div>
                )}

                <div
                  id={drawerScrollableContentWrapperId}
                  className={cn(
                    'flex grow flex-col overflow-y-auto px-40 lg:px-24 sm:px-16',
                    styles[`${theme}Scroll`],
                    contentWrapperClassName
                  )}
                >
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
)

AppDrawer.displayName = 'AppDrawer'

export default AppDrawer
